import { Loading } from "@shape-ds/components";
import { usePostHog } from "posthog-js/react";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface FeatureFlagContextProps {
  featureFlags: { [key: string]: boolean };
  setFeatureFlag: (flag: string, value: boolean) => void;
  ABTestFlags: { [key: string]: { enabled: boolean; variant: string } };
  setTestAbVariant: (flag: string, enabled: boolean, variant: string) => void;
}

const FeatureFlagContext = createContext<FeatureFlagContextProps | undefined>(
  undefined
);

export const FeatureFlagProvider: React.FC<{
  children: ReactNode;
  overrideFlags?: { [key: string]: boolean };
}> = ({ children, overrideFlags }) => {
  const posthog = usePostHog();
  const [featureFlags, setFeatureFlags] = useState<{ [key: string]: boolean }>(
    overrideFlags ?? {}
  );
  const [ABTestFlags, setABTestFlags] = useState<{
    [key: string]: { enabled: boolean; variant: string };
  }>({});
  const [isLoading, setIsLoading] = useState(!overrideFlags ? true : false);

  const updateFeatureFlags = () => {
    setIsLoading(false);
    const updatedFlags = posthog.featureFlags.getFlagsWithDetails();

    Object.entries(updatedFlags).forEach(([key, flagDetail]) => {
      if (flagDetail.variant) {
        const variant = flagDetail.enabled ? flagDetail.variant : "";
        setABTestFlags((prevFlags) => ({
          ...prevFlags,
          [key]: { enabled: flagDetail.enabled, variant },
        }));
      } else {
        setFeatureFlags((prevFlags) => ({
          ...prevFlags,
          [key]: flagDetail.enabled,
        }));
      }
    });
  };

  useEffect(() => {
    if (posthog && !overrideFlags) {
      posthog.onFeatureFlags(updateFeatureFlags);
    }
  }, [posthog]);

  const setFeatureFlag = (flag: string, value: boolean) => {
    setFeatureFlags((prevFlags) => ({ ...prevFlags, [flag]: value }));
  };
  const setABTestVariant = (
    flag: string,
    enabled: boolean,
    variant: string
  ) => {
    setABTestFlags((prevFlags) => ({
      ...prevFlags,
      [flag]: { enabled, variant },
    }));
  };

  const value = React.useMemo(
    () => ({
      featureFlags,
      setFeatureFlag,
      ABTestFlags,
      setTestAbVariant: setABTestVariant,
    }),
    [featureFlags, ABTestFlags]
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {isLoading ? <Loading center /> : children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagManager = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider"
    );
  }
  return context;
};
export const useFeatureFlag = (key: string) => {
  const context = useContext(FeatureFlagContext);
  if (context && key in context.featureFlags) {
    return context.featureFlags[key];
  }
  return false;
};
export const useAbTest = (key: string) => {
  const context = useContext(FeatureFlagContext);
  if (context && key in context.ABTestFlags) {
    return context.ABTestFlags[key];
  }
  return { enabled: false, variant: "" };
};
